import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { listCatalogs, listProducts } from "../../apis/catalog";
import { sendProductMessage } from "../../apis/chat";

const ProductPicker = ({ phoneId, customerPhone, onClose, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [catalogId, setCatalogId] = useState(null);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const catRes = await listCatalogs(phoneId);
        const connected = catRes.data?.connected_catalog_id;
        if (!connected) {
          if (!cancelled) setError("Connect a catalog on the Commerce page first.");
          return;
        }
        if (!cancelled) setCatalogId(connected);
        const prodRes = await listProducts(phoneId, { catalogId: connected });
        if (!cancelled) setProducts(prodRes.data?.products || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || "Failed to load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phoneId]);

  const filtered = products.filter((p) =>
    `${p.name || ""} ${p.retailer_id || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSend = async () => {
    if (!selected.length || !catalogId) return;
    setSending(true);
    try {
      const res = await sendProductMessage(phoneId, customerPhone, {
        catalog_id: catalogId,
        product_retailer_ids: selected,
      });
      toast.success("Product message sent");
      onSent?.(res.data?.data?.message);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to send product");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Send product</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        {loading ? (
          <div className="py-10 text-center text-gray-500">
            <Loader2 className="animate-spin inline mr-2" size={18} /> Loading products…
          </div>
        ) : error ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">{error}</p>
        ) : (
          <>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm mb-3"
            />
            <div className="overflow-y-auto flex-1 space-y-1 min-h-[160px]">
              {filtered.map((p) => {
                const id = p.retailer_id || p.retailerId;
                const on = selected.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${
                      on ? "border-emerald-400 bg-emerald-50" : "border-gray-100 hover:bg-gray-50"
                    }`}
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className="block text-xs text-gray-500 font-mono">{id}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6">No products match.</p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-xl border">Cancel</button>
              <button
                type="button"
                disabled={!selected.length || sending}
                onClick={handleSend}
                className="px-4 py-2 text-sm rounded-xl border disabled:opacity-40"
                style={{ backgroundColor: "#eefa94" }}
              >
                {sending ? "Sending…" : `Send ${selected.length || ""}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductPicker;
